import { Fragment } from 'react';
import { Lightbulb, AlertTriangle } from 'lucide-react';
import type { HelpBlock } from './types';

/** Renderiza **negrito** inline sem dependência de markdown. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} className="font-semibold text-foreground">
            {p.slice(2, -2)}
          </strong>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

export function ArticleBody({ blocks }: { blocks: HelpBlock[] }) {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading':
            return (
              <h3 key={i} className="pt-2 text-base font-semibold text-foreground">
                {b.text}
              </h3>
            );
          case 'p':
            return (
              <p key={i}>
                <RichText text={b.text} />
              </p>
            );
          case 'steps':
            return (
              <ol key={i} className="space-y-2">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-3">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
                      {j + 1}
                    </span>
                    <span className="flex-1">
                      <RichText text={it} />
                    </span>
                  </li>
                ))}
              </ol>
            );
          case 'list':
            return (
              <ul key={i} className="space-y-1.5">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />
                    <span className="flex-1">
                      <RichText text={it} />
                    </span>
                  </li>
                ))}
              </ul>
            );
          case 'tip':
            return (
              <div
                key={i}
                className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-900"
              >
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <p className="flex-1 text-[13px]">
                  <RichText text={b.text} />
                </p>
              </div>
            );
          case 'warn':
            return (
              <div
                key={i}
                className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="flex-1 text-[13px]">
                  <RichText text={b.text} />
                </p>
              </div>
            );
          case 'terms':
            return (
              <dl key={i} className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
                {b.items.map((t, j) => (
                  <div key={j} className="text-[13px]">
                    <dt className="inline font-semibold text-foreground">{t.term}: </dt>
                    <dd className="inline">{t.def}</dd>
                  </div>
                ))}
              </dl>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
